import { z } from 'zod';
import { LangCodeSchema, TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// Channel
// =============================================================================

export const ChannelKindSchema = z.enum(['dm', 'group', 'channel']);
export type ChannelKind = z.infer<typeof ChannelKindSchema>;

/** Row shape for `channels`. */
export const ChannelSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  kind: ChannelKindSchema,
  slug: z
    .string()
    .min(2)
    .max(63)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/)
    .nullable(),
  name: z.string().max(200).nullable(),
  description: z.string().max(2000).nullable(),
  is_private: z.boolean(),
  created_by: UuidSchema,
  archived_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Channel = z.infer<typeof ChannelSchema>;

/** Input for POST /channels. */
export const ChannelCreateInputSchema = z
  .object({
    kind: ChannelKindSchema,
    /** Required for kind='channel'. */
    slug: z
      .string()
      .min(2)
      .max(63)
      .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/)
      .optional(),
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).optional(),
    is_private: z.boolean().default(false),
    /** Initial member user_ids (beyond the creator who is auto-added). */
    member_ids: z.array(UuidSchema).max(100).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.kind === 'channel' && !val.slug) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['slug'],
        message: 'slug is required for kind=channel',
      });
    }
    if (val.kind === 'channel' && !val.name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'name is required for kind=channel',
      });
    }
  });
export type ChannelCreateInput = z.infer<typeof ChannelCreateInputSchema>;

/** Input for PATCH /channels/:id. */
export const ChannelUpdateInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullable(),
    is_private: z.boolean(),
  })
  .partial();
export type ChannelUpdateInput = z.infer<typeof ChannelUpdateInputSchema>;

// =============================================================================
// ChannelMember
// =============================================================================

export const ChannelMemberRoleSchema = z.enum(['member', 'admin']);
export type ChannelMemberRole = z.infer<typeof ChannelMemberRoleSchema>;

/** Row shape for `channel_members`. */
export const ChannelMemberSchema = z.object({
  channel_id: UuidSchema,
  user_id: UuidSchema,
  tenant_id: UuidSchema,
  role: ChannelMemberRoleSchema,
  joined_at: TimestampSchema,
  last_read_at: TimestampSchema.nullable(),
});
export type ChannelMember = z.infer<typeof ChannelMemberSchema>;

/** Input for POST /channels/:id/members. */
export const ChannelMemberAddInputSchema = z.object({
  user_id: UuidSchema,
  role: ChannelMemberRoleSchema.default('member'),
});
export type ChannelMemberAddInput = z.infer<typeof ChannelMemberAddInputSchema>;

// =============================================================================
// Message
// =============================================================================

/** Row shape for `messages`. */
export const MessageSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  channel_id: UuidSchema,
  sender_id: UuidSchema,
  parent_id: UuidSchema.nullable(),
  body: z.string(),
  body_lang: LangCodeSchema,
  edited_at: TimestampSchema.nullable(),
  deleted_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
});
export type Message = z.infer<typeof MessageSchema>;

/** Input for POST /channels/:id/messages. */
export const MessageCreateInputSchema = z.object({
  body: z.string().min(1).max(40_000),
  parent_id: UuidSchema.optional(),
  /** Optional override — Edge Function will auto-detect if omitted. */
  body_lang: LangCodeSchema.optional(),
});
export type MessageCreateInput = z.infer<typeof MessageCreateInputSchema>;

/** Input for PATCH /messages/:id. */
export const MessageUpdateInputSchema = z.object({
  body: z.string().min(1).max(40_000),
});
export type MessageUpdateInput = z.infer<typeof MessageUpdateInputSchema>;

/** Paginated list of messages (cursor-based, newest-first). */
export const PaginatedMessagesSchema = z.object({
  data: z.array(MessageSchema),
  /** ISO-8601 timestamp to pass as `before` on the next page request. */
  cursor: z.string().nullable(),
  /** Whether more pages exist. */
  has_more: z.boolean(),
});
export type PaginatedMessages = z.infer<typeof PaginatedMessagesSchema>;

// =============================================================================
// Reaction
// =============================================================================

/** Row shape for `message_reactions`. */
export const ReactionSchema = z.object({
  message_id: UuidSchema,
  user_id: UuidSchema,
  tenant_id: UuidSchema,
  emoji: z.string().min(1).max(64),
  created_at: TimestampSchema,
});
export type Reaction = z.infer<typeof ReactionSchema>;

/** Input for POST /messages/:id/reactions (toggle). */
export const ReactionToggleInputSchema = z.object({
  emoji: z.string().min(1).max(64),
});
export type ReactionToggleInput = z.infer<typeof ReactionToggleInputSchema>;

// =============================================================================
// Notification
// =============================================================================

export const NotificationKindSchema = z.enum([
  'mention',
  'dm',
  'assignment',
  'system',
  'ai_suggestion',
]);
export type NotificationKind = z.infer<typeof NotificationKindSchema>;

/** Row shape for `notifications`. */
export const NotificationSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  user_id: UuidSchema,
  kind: NotificationKindSchema,
  source: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  entity_type: z.string().nullable(),
  entity_id: UuidSchema.nullable(),
  action_url: z.string().nullable(),
  read_at: TimestampSchema.nullable(),
  archived_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
});
export type Notification = z.infer<typeof NotificationSchema>;
